import type { ColDef, GridApi } from 'ag-grid-community';
import React, { RefObject, useEffect, useMemo, useState } from 'react';
import { getTotalMinWidth } from '../../../utils/agGridHelpers';
import CustomPagination from './CustomPagination';

export function useScreenResizeSignal(): number {
  const [screenSize, setScreenSize] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
}

export function useWindowWidth(): number {
  return useScreenResizeSignal();
}

export function useGridPopupAutoClose(
  containerRef: RefObject<HTMLDivElement | null>,
  gridApi: GridApi | null,
  closeAllFilterPopups: () => void,
): void {
  useEffect(() => {
    if (!containerRef.current || !gridApi) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isOutsideGrid = containerRef.current && !containerRef.current.contains(target);
      const isOnPopup = target.closest('.ag-popup');

      if (isOutsideGrid && !isOnPopup) {
        closeAllFilterPopups();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [gridApi, closeAllFilterPopups, containerRef]);

  useEffect(() => {
    if (!containerRef.current || !gridApi) return;

    const handleGridClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isOnPopup = target.closest('.ag-popup');
      const isOnFilter = target.closest('.ag-floating-filter');
      const isOnHeaderFilter = target.closest('.ag-header-cell');

      if (!isOnPopup && !isOnFilter && !isOnHeaderFilter) {
        closeAllFilterPopups();
      }
    };

    const container = containerRef.current;
    container.addEventListener('click', handleGridClick, true);

    return () => {
      container.removeEventListener('click', handleGridClick, true);
    };
  }, [gridApi, closeAllFilterPopups, containerRef]);
}

export function useContainerResizeObserver(
  containerRef: RefObject<HTMLDivElement | null>,
  onResize: (_width: number) => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled || !containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        onResize(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, enabled, onResize]);
}

export function useRowHeightAutoUpdate(
  enabled: boolean,
  getDynamicRowHeight: () => number,
  refreshKey: string | number,
  setRowHeight: (_height: number) => void,
): void {
  useEffect(() => {
    if (!enabled) return;
    const rafId = window.requestAnimationFrame(() => {
      setRowHeight(getDynamicRowHeight());
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [enabled, getDynamicRowHeight, setRowHeight, refreshKey]);
}

export function useFlexSizing(columnDefs: ColDef[] = [], containerWidth: number | null) {
  const totalMinWidth = useMemo(() => getTotalMinWidth(columnDefs), [columnDefs]);
  const applyFlexSizing = containerWidth == null ? true : containerWidth >= totalMinWidth;
  return { totalMinWidth, applyFlexSizing };
}

type AgGridShellProps = {
  className?: string;
  gridHeight: string;
  containerRef: RefObject<HTMLDivElement | null>;
  containerStyle?: React.CSSProperties;
  children: React.ReactNode;
  showPagination?: boolean;
  paginationProps?: {
    page: number;
    pageCount: number;
    pageSize: number;
    onPageChange: (_args: { page: number; pageSize: number }) => void;
    onPageSizeChange: (_args: { pageSize: number }) => void;
    totalRecords?: number;
  };
};

export const AgGridShell: React.FC<AgGridShellProps> = ({
  className = '',
  gridHeight,
  containerRef,
  containerStyle,
  children,
  showPagination = false,
  paginationProps,
}) => (
  <div style={{ height: '100%', width: '100%' }}>
    <div
      className={`ag-theme-quartz ${className}`}
      style={{
        width: '100%',
        height: gridHeight,
        ...containerStyle,
      }}
      ref={containerRef}
    >
      {children}
    </div>
    {showPagination && paginationProps && (
      <CustomPagination
        page={paginationProps.page}
        pageCount={paginationProps.pageCount}
        pageSize={paginationProps.pageSize}
        onPageChange={paginationProps.onPageChange}
        onPageSizeChange={paginationProps.onPageSizeChange}
        isPageSizeVisible={true}
        totalRecords={paginationProps.totalRecords}
      />
    )}
  </div>
);
